import { api } from '../api/client';

import {
	API_COMBINED_LISTS_QUERY_URL,
	API_COMBINED_LISTS_RESOLVE_URL,
	API_COMBINED_LISTS_SUGGESTIONS_URL,
	API_COMBINED_LISTS_URL,
	API_COMBINED_LIST_ITEMS_URL,
	API_COMBINED_LIST_URL,
} from '../utils';

/**
 * COMBINED LISTS (KPE-034; keepr-api docs/SCHEMA.md § Combined lists): one
 * card across every collection a person lists — their own, the ones shared
 * with them, the global ones they follow.
 *
 * A definition is `{ match, exclude?, include? }`; `resolve` turns it into its
 * sources and the lined-up vocabulary, `query` into one page of rows from
 * every source, merged on a keyset cursor. A saved list is the same definition
 * plus `{ name, q, sort, columns }`, the person's own and private.
 *
 * Refusals ride `error.response.data` (`code`), as everywhere; the page puts
 * them into words (utils/combinedLists.js `refusalWords`).
 */

const arrayOf = value => (Array.isArray(value) ? value : []);

// Only the keys the API reads: an unknown top-level key is 400 `unknown_key`.
export function definitionBody(def = {}) {
	const body = { match: def.match };
	if (Array.isArray(def.exclude) && def.exclude.length) body.exclude = def.exclude;
	if (Array.isArray(def.include) && def.include.length) body.include = def.include;
	return body;
}

function normalizeSources(sources) {
	return arrayOf(sources).map(source => ({
		...source,
		missing: arrayOf(source?.missing),
	}));
}

/** POST /resolve — `{ match, exclude, limit, sources, vocabulary: { elements } }`. */
export async function resolveCombinedList(def, { signal } = {}) {
	const { data } = await api.post(API_COMBINED_LISTS_RESOLVE_URL, definitionBody(def), { signal });
	return {
		...data,
		sources: normalizeSources(data?.sources),
		vocabulary: { elements: arrayOf(data?.vocabulary?.elements) },
	};
}

function normalizePage(data) {
	return {
		items: arrayOf(data?.items),
		cursor: data?.cursor || null,
		sources: normalizeSources(data?.sources),
		unknown: arrayOf(data?.unknown),
	};
}

/**
 * POST /query — one page of an unsaved definition. `q` names elements by token
 * (`#k7f3q2xa`); `search` is the List's search box; `sort` is
 * `{ field, direction }`; `cursor` the previous page's.
 */
export async function queryCombinedList(def, { q, search, sort, cursor, limit } = {}, { signal } = {}) {
	const body = definitionBody(def);
	if (q) body.q = q;
	if (search) body.search = search;
	if (sort && sort.field) body.sort = sort;
	if (cursor) body.cursor = cursor;
	if (limit) body.limit = limit;
	const { data } = await api.post(API_COMBINED_LISTS_QUERY_URL, body, { signal });
	return normalizePage(data);
}

/** GET /{id}/items — a saved list's stored definition through `query`. */
export async function getCombinedListItems(id, { search, cursor, limit } = {}, { signal } = {}) {
	const params = {};
	if (search) params.search = search;
	if (cursor) params.cursor = cursor;
	if (limit) params.limit = limit;
	const { data } = await api.get(API_COMBINED_LIST_ITEMS_URL(id), { params, signal });
	return normalizePage(data);
}

/** GET / — the caller's saved lists, oldest first. `[]` on an API a deploy behind. */
export async function listCombinedLists({ signal } = {}) {
	try {
		const { data } = await api.get(API_COMBINED_LISTS_URL, { signal });
		return arrayOf(data);
	} catch (error) {
		if (error?.response?.status === 404) return [];
		throw error;
	}
}

export async function getCombinedList(id, { signal } = {}) {
	const { data } = await api.get(API_COMBINED_LIST_URL(id), { signal });
	return data;
}

/** POST / — `{ name, match, exclude?, include?, q?, sort?, columns? }` → the list. */
export async function createCombinedList(list) {
	const body = { name: list.name, ...definitionBody(list) };
	if (list.q) body.q = list.q;
	if (list.sort && list.sort.field) body.sort = list.sort;
	if (Array.isArray(list.columns) && list.columns.length) body.columns = list.columns;
	const { data } = await api.post(API_COMBINED_LISTS_URL, body);
	return data;
}

/**
 * PATCH /{id} — any of the saved fields; `null` clears `q`, `sort`, `columns`,
 * `exclude` and `include`. A body of `name` alone re-checks nothing.
 */
export async function updateCombinedList(id, patch) {
	const { data } = await api.patch(API_COMBINED_LIST_URL(id), patch);
	return data;
}

/** DELETE /{id} — the list and the caller's Home pins of it. */
export async function deleteCombinedList(id) {
	const { data } = await api.delete(API_COMBINED_LIST_URL(id));
	return data || { ok: true };
}

/**
 * GET /suggestions — `[{ match, label, sources, collections }]`: the matches
 * two or more of the caller's listed collections hold in common, never one
 * already saved, never naming a collection. `[]` on an API a deploy behind.
 */
export async function getCombinedListSuggestions({ signal } = {}) {
	try {
		const { data } = await api.get(API_COMBINED_LISTS_SUGGESTIONS_URL, { signal });
		return arrayOf(data);
	} catch (error) {
		if (error?.response?.status === 404) return [];
		throw error;
	}
}
